<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $tc('messages.prerequisites', 2),
                        href: null,
                    },
                    {
                        name: subject.name,
                        href: null,
                    },
                ]"
            />
        </template>

        <div>
            <NetworkGraph
                :edges="edges"
                :nodes="filteredTags"
                :options="{
                    edges: {
                        arrows: 'from',
                    },
                }"
            />
        </div>

        <div class="flex gap-4 mt-5">
            <label class="text-xs flex gap-1 items-center">
                <input
                    type="checkbox"
                    name="show_questions_count"
                    id=""
                    v-model="showQuestionsCount"
                    class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                />
                <span>{{ $t('messages.show_questions_count') }}</span>
            </label>
            <label class="text-xs flex gap-1 items-center">
                <input
                    type="checkbox"
                    name="hide_tags_without_questions"
                    id=""
                    v-model="hideTagsWithoutQuestions"
                    class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                />
                <span>{{ $t('messages.hide_tags_without_questions') }}</span>
            </label>
        </div>
        <PageSectionCollapsible class="mt-5" is-open>
            <template v-slot:title>
                <h2>{{ $tc('messages.content_blocks', 2) }}</h2>
            </template>

            <div class="">
                <div class="bg-gray-100 overflow-hidden rounded-lg">
                    <div class="px-4 py-5 sm:p-6">
                        <div class="flex flex-col mt-5">
                            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                                <div
                                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                                >
                                    <table class="min-w-full divide-y divide-gray-200">
                                        <thead>
                                            <tr class="text-white">
                                                <th
                                                    class="max-w-xs px-6 py-3 bg-gray-400 text-left text-xs leading-4 font-medium tracking-wider"
                                                >
                                                    {{ $tc('messages.tags', 2) }}
                                                </th>
                                                <th
                                                    class="max-w-xs px-6 py-3 bg-gray-400 text-left text-xs leading-4 font-medium tracking-wider"
                                                >
                                                    {{ $tc('messages.prerequisites', 2) }}
                                                </th>
                                                <th
                                                    class="max-w-xs px-6 py-3 bg-gray-400 text-left text-xs leading-4 font-medium tracking-wider"
                                                ></th>
                                            </tr>
                                        </thead>
                                        <template v-for="block in filteredBlocks" :key="block.id">
                                            <thead>
                                                <tr>
                                                    <th
                                                        class="max-w-xs px-6 py-3 bg-gray-300 text-left text-xs leading-4 font-medium text-gray-500 tracking-wider"
                                                        colspan="3"
                                                    >
                                                        {{ block.name }}
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody v-for="section in block.sections" :key="section.name">
                                                <tr>
                                                    <td
                                                        colspan="3"
                                                        class="max-w-xs px-6 py-3 bg-gray-200 text-left text-xs leading-4 font-medium text-gray-500 tracking-wider"
                                                    >
                                                        <span class="ml-1">{{ section.name }}</span>
                                                    </td>
                                                </tr>
                                                <tr v-for="tag in section.tags" :key="tag.id">
                                                    <td
                                                        class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500 flex items-center gap-1"
                                                    >
                                                        <input
                                                            v-model="selectedTags"
                                                            name="selected_tags"
                                                            :value="tag.id"
                                                            type="checkbox"
                                                            class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                                            :class="{ invisible: tag.tag_ids.length === 0 }"
                                                        />
                                                        <span class="">{{ tag.name }}</span>
                                                        <span v-if="showQuestionsCount" class="ml-1"
                                                            >({{ tag.questions_count }})</span
                                                        >
                                                    </td>
                                                    <td
                                                        class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500"
                                                    >
                                                        <div>
                                                            <div class="flex flex-wrap items-center gap-2">
                                                                <BadgePill
                                                                    v-for="selectedTag in allTags.filter((t) =>
                                                                        tag.tag_ids.includes(t.id),
                                                                    )"
                                                                    :key="selectedTag.id"
                                                                >
                                                                    {{ selectedTag.name }}
                                                                </BadgePill>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td
                                                        class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500"
                                                    >
                                                        <div class="flex justify-end space-x-2">
                                                            <InertiaSquareLinkButton
                                                                element="button"
                                                                action="edit"
                                                                href="/"
                                                                @click="selectedTag = tag"
                                                                v-if="meta.can.edit"
                                                            />
                                                        </div>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </template>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <template v-if="selectedTag">
                <EditTagPanel :tag="selectedTag" :tags="allTags" @close="selectedTag = null" />
            </template>
        </PageSectionCollapsible>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import NetworkGraph from '@/components/pageSpecific/NetworkGraph.vue';
import PageSectionCollapsible from '../../../../Shared/PageSectionCollapsible.vue';
import InertiaSquareLinkButton from '../../../../components/InertiaSquareLinkButton.vue';
import EditTagPanel from '../../../Admin/Categories/partials/EditTagPanel.vue';
import BadgePill from '../../../../components/BadgePill.vue';

export default {
    components: {
        BadgePill,
        EditTagPanel,
        InertiaSquareLinkButton,
        PageSectionCollapsible,
        NetworkGraph,
        PanelPage,
    },
    layout: Layout,
    name: 'Show',
    props: ['meta', 'subject', 'edges', 'tags', 'blocks', 'allTags'],
    data() {
        return {
            selectedTag: null,
            selectedTags: this.tags.map((t) => t.id),
            showQuestionsCount: true,
            hideTagsWithoutQuestions: true,
        };
    },
    computed: {
        filteredBlocks: function () {
            return this.blocks
                .map((b) => {
                    return {
                        ...b,
                        sections: b.sections
                            .map((s) => {
                                return {
                                    ...s,
                                    tags: s.tags.filter((t) => !this.hideTagsWithoutQuestions || t.questions_count > 0),
                                };
                            })
                            .filter((s) => s.tags.length),
                    };
                })
                .filter((b) => b.sections.length);
        },
        filteredTags: function () {
            return this.tags.filter((tag) => this.selectedTags.includes(tag.id));
        },
    },
    methods: {},
};
</script>
